import type {ReactNode} from 'react';
import {SlidersHorizontal} from 'lucide-react';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {Button} from '@common-web/components/ui/button';
import {LIST_COLUMNS, type GroupPipelineViewConfig} from '@common/groupPipeline/viewConfig';
import {CheckRow} from '@internal/pages/group-pipeline/components/CheckRow';

interface StepOption {
  key: string;
  label: string;
  section: string;
}

export function CustomizeView({
  activeView,
  view,
  steps,
  isCustomized,
  onChange,
  onReset,
}: {
  activeView: 'list' | 'board';
  view: GroupPipelineViewConfig;
  steps: StepOption[];
  isCustomized: boolean;
  onChange: (changes: Partial<GroupPipelineViewConfig>) => void;
  onReset: () => void;
}) {
  const sections = [...new Set(steps.map((s) => s.section))];
  const boardCount = steps.filter((s) => view.boardColumns.includes(s.key)).length;

  const toggle = <T extends string>(list: T[], value: T) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const boardSection = (
    <section>
      <SectionHeading
        title='Board columns'
        meta={`${boardCount} ${boardCount === 1 ? 'column' : 'columns'}`}
      />
      {sections.map((section) => {
        const sectionSteps = steps.filter((s) => s.section === section);
        return (
          <div key={section} className='mb-2.5 last:mb-0'>
            <p className='mb-1.5 text-xs font-bold uppercase tracking-[0.07em] text-gray-500'>
              {section} steps
            </p>
            <OptionGrid count={sectionSteps.length}>
              {sectionSteps.map((step) => (
                <CheckRow
                  key={step.key}
                  label={step.label}
                  checked={view.boardColumns.includes(step.key)}
                  onToggle={() => onChange({boardColumns: toggle(view.boardColumns, step.key)})}
                />
              ))}
            </OptionGrid>
          </div>
        );
      })}
    </section>
  );

  const listSection = (
    <section>
      <SectionHeading title='List columns' />
      <OptionGrid count={LIST_COLUMNS.length + 1}>
        <CheckRow label='Group' checked disabled />
        {LIST_COLUMNS.map((column) => (
          <CheckRow
            key={column.key}
            label={column.label}
            checked={view.listColumns.includes(column.key)}
            onToggle={() => onChange({listColumns: toggle(view.listColumns, column.key)})}
          />
        ))}
      </OptionGrid>
    </section>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' className='bg-white'>
          <SlidersHorizontal className='mr-1.5 h-4 w-4' />
          Customize view
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        className='w-[480px] max-w-[calc(100vw-2rem)] rounded-[14px] border-gray-200 p-0 shadow-[0_16px_40px_rgba(15,23,42,0.16)]'
      >
        <div className='max-h-[70vh] overflow-y-auto p-4'>
          {activeView === 'list' ? listSection : boardSection}
        </div>
        <div className='flex items-center border-t border-gray-200 px-4 py-3'>
          <button
            type='button'
            disabled={!isCustomized}
            onClick={onReset}
            className='text-sm font-semibold text-gray-500 hover:text-gray-900 disabled:cursor-default disabled:opacity-50 disabled:hover:text-gray-500'
          >
            Reset to defaults
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SectionHeading({title, meta}: {title: string; meta?: string}) {
  return (
    <div className='mb-2.5 flex items-center'>
      <h3 className='flex-1 text-[15px] font-bold text-gray-900'>{title}</h3>
      {meta && <span className='text-sm font-semibold tabular-nums text-gray-500'>{meta}</span>}
    </div>
  );
}

// Fill down each column before moving to the next.
function OptionGrid({count, children}: {count: number; children: ReactNode}) {
  return (
    <div
      className='grid grid-flow-col grid-cols-2 gap-x-2'
      style={{gridTemplateRows: `repeat(${Math.ceil(count / 2)}, auto)`}}
    >
      {children}
    </div>
  );
}
